import { FormControl } from '../../../../../../widgets/formControl';
import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import OptionIcon from 'src/shared/material_icons/image/image.svg';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import styled from 'styled-components';
import {
    ComposerAddButton,
    ComposerAddCard,
    ComposerEmptyList,
    ComposerFieldNote,
    ComposerIconButton,
    ComposerSettingsBody,
    ComposerToolbar,
    ComposerToolbarSpacer,
    composerFieldDensity
} from '../composerWorkspace';
import { ChevronIcon, CollapseAllIcon, ExpandAllIcon, SingleBranchIcon } from 'src/components/widgets/icons/scene';
import { ComposerChoice, ComposerSlot, ComposerSlotList, ComposerSlotOption } from '../composerSlots';
import {
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator,
    getComposerLinkOptionIDs,
    isComposerMultiOptionLink,
    joinComposerLinkOptionIDs
} from './composerAttributesModels';
import { ComposerValidationErrorCode } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';

const Rules = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    ${composerFieldDensity}
`;

const Intro = styled.p`
    margin: 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 12px;
    line-height: 18px;
`;

const Expressions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

// OGNI ESPRESSIONE È UNA CARD (round 4, pacchetto I): in testa l'attributo che decide, sotto come lo
// si confronta e con quale opzione. Era una riga a tre colonne sotto un'intestazione da tabella, e in
// una colonna stretta i tre menu non si leggevano.
const ExpressionCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    background: var(--composer-panel, #fff);
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
`;

const ExpressionHead = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 28px 28px;
    align-items: start;
    gap: 6px;
`;

// SOTTO L'ATTRIBUTO, IL CONFRONTO E LE OPZIONI (prototipo V2, «Match» e «Choice»): il confronto a
// segmenti a tutta riga, le opzioni come slot — una, o una per riga con «is one of».
const ExpressionBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ApplyNote = styled.p`
    margin: 6px 0 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 11px;
    line-height: 16px;
`;

const ExpressionConnector = styled.span`
    padding-left: 8px;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

// LA FRECCIA DELLA CARD GUARDA IN GIÙ DA CHIUSA, come quella delle card delle azioni.
const CardChevron = styled(ChevronIcon)<{ $open: boolean }>`
    transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
    transition: transform 160ms ease;
`;

interface ComposerLinksEditorProps {
    attributes: ComposerAttributeNode[];
    links: ComposerAttributeNodeLink[];
    logicalOperator?: ComposerLogicalOperator;
    selfAttributeID?: string;
    subjectLabel: string;
    validationErrors?: ReadonlyMap<string, string>;
    onChange: (links: ComposerAttributeNodeLink[], logicalOperator: ComposerLogicalOperator) => void;
}

const ComposerLinksEditor: FC<ComposerLinksEditorProps> = ({
    attributes,
    links,
    logicalOperator = ComposerLogicalOperator.AND,
    selfAttributeID,
    subjectLabel,
    validationErrors,
    onChange
}) => {
    const availableAttributes = attributes.filter(
        (attribute) => attribute.type === ComposerAttributeType.Attribute && attribute.id !== selfAttributeID && attribute.options.length > 0
    );
    const attributeOptions: ComposerSlotOption[] = availableAttributes.map((attribute) => ({
        value: attribute.id,
        label: attribute.name,
        caption: attribute.options.length === 1 ? T._('1 option', 'Admin') : stringFormat(T._('{0} options', 'Admin'), attribute.options.length.toString())
    }));
    // «APPLY IF» È UNA SCELTA A DUE (prototipo V2): All / Any, e sotto che cosa vuol dire. Con meno di due
    // espressioni non c'è niente da combinare, e la scelta resta spenta.
    const logicalOptions = [
        { value: ComposerLogicalOperator.AND, label: T._('All', 'Admin') },
        { value: ComposerLogicalOperator.OR, label: T._('Any', 'Admin') }
    ];
    const logicalSays =
        logicalOperator === ComposerLogicalOperator.OR
            ? T._('Any of the following expressions is true', 'Admin')
            : T._('All of the following expressions are true', 'Admin');
    // «is / is not», come lo dice il prototipo: si legge di seguito, «Colore is Rosso».
    //
    // «IS ONE OF / IS NOT ONE OF» NON SI OFFRONO (utente, 2026-10-15). Il motore 3D li valuta (In / NotIn,
    // `ComposerSelection`), ma il Backoffice ha sempre scritto solo = e ≠, e che il backend salvi e usi gli
    // altri due non è verificato. Un'espressione che li porta già — scritta altrove — si legge e si modifica
    // ancora, con le sue opzioni una per riga; il confronto si può riportare a «is» o «is not».
    const comparisonOptions = [
        { value: ComposerComparisonOperator.EQUAL, label: T._('is', 'Admin') },
        { value: ComposerComparisonOperator.NOT_EQUAL, label: T._('is not', 'Admin') }
    ];
    const connectorLabel = logicalOperator === ComposerLogicalOperator.OR ? T._('or', 'Admin') : T._('and', 'Admin');
    const hasLinkError = (index: number, ...codes: string[]) => codes.some((code) => validationErrors?.has(`${code}:${index}`));

    const updateLink = (index: number, nextLink: ComposerAttributeNodeLink) => {
        const nextLinks = links.map((link, linkIndex) => (linkIndex === index ? nextLink : link));
        onChange(nextLinks, logicalOperator);
    };

    // PASSANDO DA UNA A PIÙ OPZIONI LA SCELTA NON SI PERDE: «is Rosso» diventa «is one of Rosso», e tornando
    // indietro resta la prima delle scelte.
    const changeOperation = (index: number, link: ComposerAttributeNodeLink, operation: ComposerComparisonOperator) => {
        const optionIDs = getComposerLinkOptionIDs(link);
        const optionID = isComposerMultiOptionLink(operation) ? joinComposerLinkOptionIDs(optionIDs) : (optionIDs[0] ?? '');
        updateLink(index, { ...link, operation, optionID });
    };

    // A new expression starts empty, so the placeholders ask for a conscious choice instead of
    // silently linking the first attribute; validation flags it until both selects are filled.
    const addLink = () => {
        onChange([...links, { attributeID: '', optionID: '', operation: ComposerComparisonOperator.EQUAL }], logicalOperator);
        openCard(links.length);
    };

    // LE ESPRESSIONI SONO CARD CHE SI CHIUDONO, come le azioni (utente, 2026-10-15): la barra in alto ha
    // «Add», una card alla volta (acceso di partenza) e apri/chiudi tutte. Chiusa, la card dice in una riga
    // che cosa chiede — «is Red» sotto il nome dell'attributo.
    const [openIndexes, setOpenIndexes] = React.useState<number[]>([]);
    const [singleCard, setSingleCard] = React.useState(true);
    const openCard = (index: number) => setOpenIndexes((current) => (singleCard ? [index] : current.includes(index) ? current : [...current, index]));
    const toggleCard = (index: number) =>
        openIndexes.includes(index) ? setOpenIndexes((current) => current.filter((candidate) => candidate !== index)) : openCard(index);
    const removeLink = (index: number) => {
        onChange(
            links.filter((_, linkIndex) => linkIndex !== index),
            logicalOperator
        );
        setOpenIndexes((current) => current.filter((candidate) => candidate !== index).map((candidate) => (candidate > index ? candidate - 1 : candidate)));
    };
    const summaryOf = (link: ComposerAttributeNodeLink, attribute?: ComposerAttributeNode) => {
        if (!attribute) return undefined;
        const names = getComposerLinkOptionIDs(link)
            .map((id) => attribute.options.find((option) => option.id === id)?.name)
            .filter(Boolean)
            .join(', ');
        const verb = comparisonOptions.find((option) => option.value === link.operation)?.label ?? link.operation;
        return names ? `${verb} ${names}` : stringFormat(T._('{0} — no option yet', 'Admin'), verb);
    };

    return (
        <>
            <ComposerToolbar>
                <ComposerAddButton disabled={availableAttributes.length === 0} onClick={addLink}>
                    <PlusIcon />
                    {T._('Add', 'Admin')}
                </ComposerAddButton>
                <ComposerToolbarSpacer />
                <ZTooltip title={singleCard ? T._('One card at a time: opening a card closes the others', 'Admin') : T._('One card at a time', 'Admin')}>
                    <ComposerIconButton
                        aria-label={T._('One card at a time', 'Admin')}
                        aria-pressed={singleCard}
                        $active={singleCard}
                        onClick={() => {
                            if (!singleCard) setOpenIndexes([]);
                            setSingleCard(!singleCard);
                        }}
                    >
                        <SingleBranchIcon />
                    </ComposerIconButton>
                </ZTooltip>
                <ZTooltip
                    title={
                        singleCard && openIndexes.length === 0
                            ? T._('One card at a time is on: open a single card from its head', 'Admin')
                            : openIndexes.length > 0
                              ? T._('Collapse all', 'Admin')
                              : T._('Expand all', 'Admin')
                    }
                >
                    <ComposerIconButton
                        aria-label={openIndexes.length > 0 ? T._('Collapse all', 'Admin') : T._('Expand all', 'Admin')}
                        disabled={links.length === 0 || (singleCard && openIndexes.length === 0)}
                        onClick={() => setOpenIndexes(openIndexes.length > 0 ? [] : links.map((_, index) => index))}
                    >
                        {openIndexes.length > 0 ? <CollapseAllIcon /> : <ExpandAllIcon />}
                    </ComposerIconButton>
                </ZTooltip>
            </ComposerToolbar>
            <ComposerSettingsBody>
                <Rules>
                    <Intro>
                        {T._(
                            `${subjectLabel} is shown in the configurator only when these expressions are met, for example when a given option of another attribute is selected. Otherwise it is hidden.`,
                            'Admin'
                        )}
                    </Intro>
                    <FormControl label={T._('Apply if', 'Admin')}>
                        <ComposerChoice
                            ariaLabel={T._('Apply if', 'Admin')}
                            options={logicalOptions}
                            value={logicalOperator}
                            disabled={links.length < 2}
                            onChange={(operator) => onChange(links, operator)}
                        />
                        <ApplyNote>{logicalSays}</ApplyNote>
                    </FormControl>
                    {links.length > 0 ? (
                        <Expressions>
                            {links.map((link, index) => {
                                const linkedAttribute = availableAttributes.find((attribute) => attribute.id === link.attributeID);
                                const optionOptions: ComposerSlotOption[] = (linkedAttribute?.options ?? []).map((option) => ({
                                    value: option.id,
                                    label: option.name,
                                    preview: option.img ? <img src={option.img} alt='' /> : undefined
                                }));
                                const multiple = isComposerMultiOptionLink(link.operation);
                                const chosenIDs = getComposerLinkOptionIDs(link);
                                const optionError = hasLinkError(
                                    index,
                                    ComposerValidationErrorCode.LINK_INCOMPLETE,
                                    ComposerValidationErrorCode.LINK_OPTION_NOT_FOUND
                                );
                                const isOpen = openIndexes.includes(index);
                                const summary = summaryOf(link, linkedAttribute);
                                const headOptions = attributeOptions.map((option) =>
                                    option.value === link.attributeID && summary ? { ...option, caption: summary } : option
                                );
                                return (
                                    // La chiave non porta le opzioni: scegliendone una in più il menu deve restare aperto.
                                    <React.Fragment key={`${index}_${link.attributeID}`}>
                                        {index > 0 && <ExpressionConnector>{connectorLabel}</ExpressionConnector>}
                                        <ExpressionCard>
                                            <ExpressionHead>
                                                <ComposerSlot
                                                    id={`composer-link-${index}-attribute`}
                                                    value={link.attributeID || undefined}
                                                    options={headOptions}
                                                    glyph={<AttributeIcon />}
                                                    emptyLabel={T._('Choose an attribute', 'Admin')}
                                                    searchPlaceholder={T._('Search attributes…', 'Admin')}
                                                    missingLabel={T._('Missing attribute', 'Admin')}
                                                    isError={hasLinkError(
                                                        index,
                                                        ComposerValidationErrorCode.LINK_INCOMPLETE,
                                                        ComposerValidationErrorCode.LINK_ATTRIBUTE_NOT_FOUND,
                                                        ComposerValidationErrorCode.LINK_SELF_REFERENCE
                                                    )}
                                                    onChange={(attributeID) => updateLink(index, { ...link, attributeID, optionID: '' })}
                                                />
                                                <ZTooltip title={T._('Remove expression', 'Admin')}>
                                                    <ComposerIconButton
                                                        $danger
                                                        aria-label={T._('Remove expression', 'Admin')}
                                                        onClick={() => removeLink(index)}
                                                    >
                                                        <Icon>
                                                            <DeleteIcon />
                                                        </Icon>
                                                    </ComposerIconButton>
                                                </ZTooltip>
                                                <ComposerIconButton
                                                    aria-label={isOpen ? T._('Collapse', 'Admin') : T._('Expand', 'Admin')}
                                                    aria-expanded={isOpen}
                                                    onClick={() => toggleCard(index)}
                                                >
                                                    <CardChevron $open={isOpen} />
                                                </ComposerIconButton>
                                            </ExpressionHead>
                                            {isOpen && (
                                                <ExpressionBody>
                                                    <ComposerChoice
                                                        ariaLabel={T._('Operation', 'Admin')}
                                                        options={comparisonOptions}
                                                        value={link.operation}
                                                        onChange={(operation) => changeOperation(index, link, operation)}
                                                    />
                                                    {!linkedAttribute ? (
                                                        <ApplyNote>{T._('Choose an attribute first.', 'Admin')}</ApplyNote>
                                                    ) : multiple ? (
                                                        <ComposerSlotList
                                                            id={`composer-link-${index}-options`}
                                                            value={chosenIDs}
                                                            options={optionOptions}
                                                            glyph={<OptionIcon />}
                                                            caption={linkedAttribute.name}
                                                            emptyLabel={T._('Choose an option', 'Admin')}
                                                            searchPlaceholder={T._('Search options…', 'Admin')}
                                                            missingLabel={T._('Missing option', 'Admin')}
                                                            isError={optionError}
                                                            onChange={(ids) => updateLink(index, { ...link, optionID: joinComposerLinkOptionIDs(ids) })}
                                                        />
                                                    ) : (
                                                        <ComposerSlot
                                                            id={`composer-link-${index}-option`}
                                                            value={link.optionID || undefined}
                                                            options={optionOptions}
                                                            glyph={<OptionIcon />}
                                                            caption={linkedAttribute.name}
                                                            emptyLabel={T._('Choose an option', 'Admin')}
                                                            searchPlaceholder={T._('Search options…', 'Admin')}
                                                            missingLabel={T._('Missing option', 'Admin')}
                                                            isError={optionError}
                                                            onChange={(optionID) => updateLink(index, { ...link, optionID })}
                                                        />
                                                    )}
                                                </ExpressionBody>
                                            )}
                                        </ExpressionCard>
                                    </React.Fragment>
                                );
                            })}
                        </Expressions>
                    ) : (
                        // A LISTA VUOTA il tratteggiato è il posto della prima espressione; con delle card, «Add» sta in alto.
                        <ComposerEmptyList>
                            <ComposerAddCard type='button' disabled={availableAttributes.length === 0} onClick={addLink}>
                                <PlusIcon />
                                {T._('Add an expression', 'Admin')}
                            </ComposerAddCard>
                            <ComposerFieldNote>{T._(`No expressions yet: ${subjectLabel.toLowerCase()} is always shown.`, 'Admin')}</ComposerFieldNote>
                        </ComposerEmptyList>
                    )}
                </Rules>
            </ComposerSettingsBody>
        </>
    );
};

export default ComposerLinksEditor;
